/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import { defineMessages } from 'react-intl'
import {
    LocationIcon,
    ReceiptIcon,
    HeartIcon,
    LockIcon
} from '@salesforce/retail-react-app/app/components/icons'
import {Account, InfoCircle} from '../../components/icons'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

export const messages = defineMessages({
    orders: { defaultMessage: 'Order history', id: 'global.account.link.order_history' },
    wishlist: { defaultMessage: 'Wishlist', id: 'global.account.link.wishlist' },
    profile: { defaultMessage: 'Personal Information', id: 'global.account.link.account_info' },
    password: { defaultMessage: 'Change Password', id: 'global.account.link.password' },
    addresses: { defaultMessage: 'Address Book', id: 'global.account.link.addresses' },
    payment: { defaultMessage: 'Payment Options', id: 'global.account.link.payment' },
    email: { defaultMessage: 'Communication Preferences', id: 'global.account.communication.preferences' }
})

export const INTL_HIDDEN_ACCOUNT_NAV_LINKS = new Set(['addresses', 'payment'])

export const INTL_BLOCKED_ACCOUNT_PATHS = ['/addresses', '/payment']

export const getNavLinks = ({isInternational = false} = {}) => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const SMS_OPTIN_LOYALTY_ENABLED = getSiteEnvVarValue(appConfig, 'SMS_OPTIN_LOYALTY_ENABLED', siteId)
    const SFSC_EMAIL_SUBSCRIPTION_ENABLED = getSiteEnvVarValue(appConfig, 'SFSC_EMAIL_SUBSCRIPTION_ENABLED', siteId)

    return [
        {
            name: 'orders',
            path: '/orders',
            icon: ReceiptIcon
        },
        {
            name: 'wishlist',
            path: '/wishlist',
            icon: HeartIcon
        },
        {
            name: 'profile',
            path: '/personal',
            icon: Account
        },
        {
            name: 'password',
            path: '/password',
            icon: LockIcon
        },
        {
            name: 'addresses',
            path: '/addresses',
            icon: LocationIcon
        },
        {
            name: 'payment',
            path: '/payment',
            icon: LockIcon
        },
        ...(SMS_OPTIN_LOYALTY_ENABLED || SFSC_EMAIL_SUBSCRIPTION_ENABLED
            ? [
                  {
                      name: 'email',
                      path: '/email',
                      icon: InfoCircle
                  }
              ]
            : [])
    ].filter((link) => !(isInternational && INTL_HIDDEN_ACCOUNT_NAV_LINKS.has(link.name)))
}

export const CONFIRMATION_DIALOG_DEFAULT_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Confirm Action',
        id: 'confirmation_modal.default.title.confirm_action'
    },
    confirmationMessage: {
        defaultMessage: 'Are you sure you want to continue?',
        id: 'confirmation_modal.default.message.you_want_to_continue'
    },
    primaryActionLabel: { defaultMessage: 'Yes', id: 'confirmation_modal.default.action.yes' },
    primaryActionAriaLabel: {
        defaultMessage: 'Yes, confirm action',
        id: 'confirmation_modal.default.assistive_msg.yes'
    },
    alternateActionLabel: { defaultMessage: 'No', id: 'confirmation_modal.default.action.no' },
    alternateActionAriaLabel: {
        defaultMessage: 'No, cancel action',
        id: 'confirmation_modal.default.assistive_msg.no'
    }
})

export const CREDIT_CARD = 'credit card'
export const PAYPAL = 'paypal'
export const APPLE_PAY = 'applepay'
export const GIFT_CARD = 'smartclixx'
export const BRAND_NAME = {
    GARAGE: 'Garage',
    DYNAMITE: 'Dynamite'
}
export const CASH = 'cash'

export const ORDER_RAW_STATUS = {
    TRACKING: ['released', 'partially released'],
    STATUS: ['back ordered', 'allocated', 'fulfilled']
}

export const ORDER_RETURN_RAW_STATUS = {
    RETURNED: 'returned',
    PENDING_RETURN: 'pending return',
    PARTIALLY_RETURNED: 'partially returned',
    PARTIALLY_PENDING_RETURN: 'partially pending return',
    CANCELED: 'canceled',
    CANCELLED: 'cancelled'
}

export const ORDER_PRODUCTS_RENDER_MODES = {
    PACKAGES: 'packages',
    ITEMS: 'items'
}

export const SHIPPING_METHODS = {
    PICKUP_IN_STORE: {
        ID: 'pickUpInStore',
        DISPLAY_NAME: 'Pick Up In Store',
        EST_ARRIVAL_TIME: '(Available in 2 hours)'
    }
}

export const RETURNABLE_WINDOW_EXCEEDED_REASON = 'returns.window.exceeded'
export const RETURN_LABEL_URL = 'https://www.royalmail.com/track-my-return#/details/8832'

export const COUNTRY = {
    GB: 'United Kingdom',
    CA: 'Canada',
    US: 'United States'
}
export const PAYMENT_METHODS = {
    GIFT_CARD: 'gift card'
}

export const DSS = {
    ACTIONS: {
        CANCELLATION: 'cancellation',
        CHANGE_ADDRESS: 'changeaddress',
        TRACKING: 'tracking',
        RETURNS: 'returns'

    },
    SDK_SRC: 'https://cdn.manh.cloud/selfservice/websdk/latest/selfservice.js'
}
